import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PatientSidebar from './PatientSidebar';
import PatientDashboardHeader from './PatientDashboardHeader';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useBaseUrl } from '../utils/utils';

// Define interfaces
interface ConsentRequest {
    id: string;
    patient: {
        patient_id: string;
        first_name: string;
        last_name: string;
        email: string;
    };
    medical_record: {
        id: string;
        name: string;
        record_identifier: string;
        chief_complaint: string;
        date_of_service: string;
        privacy_level: string;
        clinic: {
            id: number;
            name: string;
        };
    };
    consented_to: {
        id: number;
        name: string;
        email: string;
        phone: string;
    };
    status: string;
    purpose: string;
    scope: string;
    created_at: string;
    start_date?: string;
    end_date?: string;
}

const PatientConsents: React.FC = () => {
    const [pendingRequests, setPendingRequests] = useState<ConsentRequest[]>([]);
    const [consentHistory, setConsentHistory] = useState<ConsentRequest[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');

    const base_url = useBaseUrl();

    useEffect(() => {
        document.title = "My Consent Requests - Uzima Patient Portal";
        validatePatientAndLoadData();
    }, []);

    const validatePatientAndLoadData = async () => {
        try {
            // Validate patient authentication
            const patientId = localStorage.getItem('patient_id');
            const patientToken = localStorage.getItem('patient_token');
            
            if (!patientId || !patientToken) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Required",
                    text: "Please log in to access your consent requests",
                    confirmButtonText: "Go to Login"
                }).then(() => {
                    window.location.href = "/patient/login";
                });
                return;
            }
            
            await loadConsentData();
        } catch (error) {
            console.error("Authentication validation failed:", error);
            Swal.fire({
                icon: "error",
                title: "Authentication Error",
                text: "Unable to validate your access. Please log in again."
            });
        }
    };

    const loadConsentData = async () => {
        await Promise.all([
            loadPendingRequests(),
            loadConsentHistory()
        ]);
    };

    // Load pending consent requests
    const loadPendingRequests = async () => {
        try {
            // In a real app, you'd get the patient ID from authentication context
            const patientId = localStorage.getItem('patient_id') || 'PAT001'; // Placeholder
            
            const response = await axios.get(
                `${base_url}ehr/api/v1/share-requests/pending_requests/?patient_id=${patientId}`
            );

            if (response.data.status === "success") {
                setPendingRequests(response.data.data || []);
            }
        } catch (error: any) {
            console.error("Error loading pending requests:", error);
        }
    };

    // Load consent history
    const loadConsentHistory = async () => {
        try {
            const patientId = localStorage.getItem('patient_id') || 'PAT001'; // Placeholder
            
            const response = await axios.get(
                `${base_url}ehr/api/v1/patient-consents/?patient_id=${patientId}`
            );

            if (response.data.status === "success") {
                const history = response.data.data.filter((consent: ConsentRequest) => 
                    consent.status !== 'pending'
                );
                setConsentHistory(history);
            }
        } catch (error: any) {
            console.error("Error loading consent history:", error);
        }
    };

    // Approve consent request
    const approveRequest = async (requestId: string) => {
        const result = await Swal.fire({
            title: 'Approve Sharing Request',
            text: 'Do you want to approve sharing your medical record with this facility?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#dc3545',
            confirmButtonText: 'Yes, Approve',
            cancelButtonText: 'Cancel',
            input: 'date',
            inputLabel: 'Access valid until (optional):',
            inputPlaceholder: 'Leave empty for indefinite access'
        });

        if (result.isConfirmed) {
            setIsLoading(true);

            try {
                // Validate authentication before making request
                const patientId = localStorage.getItem('patient_id');
                const patientToken = localStorage.getItem('patient_token');
                
                if (!patientId || !patientToken) {
                    throw new Error("Authentication session expired. Please log in again.");
                }

                const approvalData: any = {};
                if (result.value) {
                    // Validate end date
                    const endDate = new Date(result.value);
                    if (endDate <= new Date()) {
                        throw new Error("End date must be in the future");
                    }
                    approvalData.end_date = result.value;
                }

                // Add authentication headers
                const authHeaders = {
                    'Content-Type': 'application/json',
                    'X-Patient-ID': patientId?.toString() || '',
                    'Authorization': `Bearer ${patientToken || ''}`
                };

                const response = await axios.post(
                    `${base_url}ehr/api/v1/share-requests/${requestId}/approve_request/`,
                    approvalData,
                    { headers: authHeaders }
                );

                if (response.data.status === "success") {
                    await Swal.fire({
                        icon: 'success',
                        title: 'Request Approved',
                        text: 'The facility now has access to your medical record.',
                        timer: 3000,
                        showConfirmButton: false
                    });

                    // Reload data
                    await loadConsentData();
                } else {
                    throw new Error(response.data.message || "Failed to approve request");
                }
            } catch (error: any) {
                console.error("Error approving request:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to approve request'
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Reject consent request
    const rejectRequest = async (requestId: string) => {
        const result = await Swal.fire({
            title: 'Reject Sharing Request',
            text: 'Why are you rejecting this request?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Reject',
            cancelButtonText: 'Cancel',
            input: 'textarea',
            inputLabel: 'Reason for rejection (optional):',
            inputPlaceholder: 'Enter your reason for rejecting this request...'
        });

        if (result.isConfirmed) {
            setIsLoading(true);

            try {
                // Validate authentication before making request
                const patientId = localStorage.getItem('patient_id');
                const patientToken = localStorage.getItem('patient_token');
                
                if (!patientId || !patientToken) {
                    throw new Error("Authentication session expired. Please log in again.");
                }

                const reason = (result.value || 'Rejected by patient').trim();
                
                // Validate rejection reason length
                if (reason.length > 500) {
                    throw new Error("Rejection reason cannot exceed 500 characters");
                }

                const rejectionData = {
                    reason: reason
                };

                // Add authentication headers
                const authHeaders = {
                    'Content-Type': 'application/json',
                    'X-Patient-ID': patientId?.toString() || '',
                    'Authorization': `Bearer ${patientToken || ''}`
                };

                const response = await axios.post(
                    `${base_url}ehr/api/v1/share-requests/${requestId}/reject_request/`,
                    rejectionData,
                    { headers: authHeaders }
                );

                if (response.data.status === "success") {
                    await Swal.fire({
                        icon: 'success',
                        title: 'Request Rejected',
                        text: 'The sharing request has been rejected.',
                        timer: 3000,
                        showConfirmButton: false
                    });

                    // Reload data
                    await loadConsentData();
                } else {
                    throw new Error(response.data.message || "Failed to reject request");
                }
            } catch (error: any) {
                console.error("Error rejecting request:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to reject request'
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Format date function
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get status badge
    const getStatusBadge = (status: string) => {
        const statusConfig = {
            pending: { class: "warning", text: "Pending Your Decision" },
            active: { class: "success", text: "Active - Sharing Approved" },
            rejected: { class: "danger", text: "Rejected" },
            expired: { class: "secondary", text: "Expired" },
            inactive: { class: "dark", text: "Inactive" }
        };
        
        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
        return <span className={`badge bg-${config.class}`}>{config.text}</span>;
    };

    return (
        <div className="d-flex">
            <PatientSidebar />
            
            <div className="flex-grow-1" style={{ marginLeft: '250px' }}>
                <PatientDashboardHeader />
                
                <div className="container-fluid p-4">
                    {/* Page Header */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h2 className="mb-1">
                                        <i className="fas fa-shield-check me-2 text-primary"></i>
                                        Medical Record Consent Management
                                    </h2>
                                    <p className="text-muted">
                                        Manage who can access your medical records and for what purpose
                                    </p>
                                </div>
                                <Link to="/patient/dashboard" className="btn btn-outline-secondary">
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Back to Dashboard
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <ul className="nav nav-pills nav-fill bg-light rounded p-1">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'pending' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('pending')}
                                    >
                                        <i className="fas fa-clock me-2"></i>
                                        Pending Requests ({pendingRequests.length})
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('history')}
                                    >
                                        <i className="fas fa-history me-2"></i>
                                        Consent History ({consentHistory.length})
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Pending Requests Tab */}
                    {activeTab === 'pending' && (
                        <div className="row">
                            <div className="col-12">
                                {pendingRequests.length === 0 ? (
                                    <div className="card">
                                        <div className="card-body text-center py-5">
                                            <i className="fas fa-inbox display-4 text-muted mb-3"></i>
                                            <h5 className="text-muted">No Pending Requests</h5>
                                            <p className="text-muted">
                                                You have no pending medical record sharing requests at this time.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    pendingRequests.map((request) => (
                                        <div key={request.id} className="card mb-4 border-warning">
                                            <div className="card-header bg-warning-subtle">
                                                <div className="row align-items-center">
                                                    <div className="col">
                                                        <h6 className="mb-0">
                                                            <i className="fas fa-exclamation-triangle me-2"></i>
                                                            New Sharing Request - Action Required
                                                        </h6>
                                                    </div>
                                                    <div className="col-auto">
                                                        {getStatusBadge(request.status)}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-8">
                                                        <div className="mb-3">
                                                            <h6 className="text-primary">Medical Record Details</h6>
                                                            <div className="ps-3">
                                                                <p className="mb-1">
                                                                    <strong>Record:</strong> {request.medical_record.name}
                                                                </p>
                                                                <p className="mb-1">
                                                                    <strong>Chief Complaint:</strong> {request.medical_record.chief_complaint}
                                                                </p>
                                                                <p className="mb-1">
                                                                    <strong>Service Date:</strong> {formatDate(request.medical_record.date_of_service)}
                                                                </p>
                                                                <p className="mb-0">
                                                                    <strong>Privacy Level:</strong> 
                                                                    <span className={`badge ms-2 ${request.medical_record.privacy_level === 'confidential' ? 'bg-danger' : 'bg-info'}`}>
                                                                        {request.medical_record.privacy_level}
                                                                    </span>
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="mb-3">
                                                            <h6 className="text-primary">Requesting Facility</h6>
                                                            <div className="ps-3">
                                                                <p className="mb-1">
                                                                    <strong>Facility:</strong> {request.consented_to.name}
                                                                </p>
                                                                <p className="mb-1">
                                                                    <strong>Contact:</strong> {request.consented_to.email}
                                                                </p>
                                                                <p className="mb-0">
                                                                    <strong>Phone:</strong> {request.consented_to.phone}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="mb-3">
                                                            <h6 className="text-primary">Purpose & Scope</h6>
                                                            <div className="ps-3">
                                                                <p className="mb-2">
                                                                    <strong>Purpose:</strong> {request.purpose}
                                                                </p>
                                                                <div>
                                                                    <strong>What will be shared:</strong>
                                                                    <div className="mt-2 p-3 bg-light rounded">
                                                                        <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>
                                                                            {request.scope}
                                                                        </pre>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="alert alert-info">
                                                            <i className="fas fa-info-circle me-2"></i>
                                                            <strong>Your Rights:</strong> You can approve or reject this request. 
                                                            If approved, you can revoke access at any time from your consent history.
                                                        </div>
                                                    </div>

                                                    <div className="col-md-4">
                                                        <div className="card border-0 bg-light">
                                                            <div className="card-header bg-transparent">
                                                                <h6 className="mb-0">
                                                                    <i className="fas fa-clock me-2"></i>
                                                                    Request Timeline
                                                                </h6>
                                                            </div>
                                                            <div className="card-body">
                                                                <p className="mb-2">
                                                                    <strong>Requested:</strong><br />
                                                                    <small>{formatDate(request.created_at)}</small>
                                                                </p>
                                                                <p className="mb-0">
                                                                    <strong>Status:</strong><br />
                                                                    <small>Awaiting your decision</small>
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="mt-3 d-grid gap-2">
                                                            <button
                                                                className="btn btn-success"
                                                                onClick={() => approveRequest(request.id)}
                                                                disabled={isLoading}
                                                            >
                                                                <i className="fas fa-check me-2"></i>
                                                                Approve Request
                                                            </button>
                                                            <button
                                                                className="btn btn-danger"
                                                                onClick={() => rejectRequest(request.id)}
                                                                disabled={isLoading}
                                                            >
                                                                <i className="fas fa-times me-2"></i>
                                                                Reject Request
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Consent History Tab */}
                    {activeTab === 'history' && (
                        <div className="row">
                            <div className="col-12">
                                {consentHistory.length === 0 ? (
                                    <div className="card">
                                        <div className="card-body text-center py-5">
                                            <i className="fas fa-history display-4 text-muted mb-3"></i>
                                            <h5 className="text-muted">No Consent History</h5>
                                            <p className="text-muted">
                                                You have no consent history at this time.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="card">
                                        <div className="card-header">
                                            <h6 className="mb-0">
                                                <i className="fas fa-list me-2"></i>
                                                Your Consent History
                                            </h6>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="table-responsive">
                                                <table className="table table-hover mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Medical Record</th>
                                                            <th>Shared With</th>
                                                            <th>Purpose</th>
                                                            <th>Status</th>
                                                            <th>Decision Date</th>
                                                            <th>Access Until</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {consentHistory.map((consent) => (
                                                            <tr key={consent.id}>
                                                                <td>
                                                                    <div>
                                                                        <strong>{consent.medical_record.name}</strong>
                                                                        <br />
                                                                        <small className="text-muted">
                                                                            {consent.medical_record.record_identifier}
                                                                        </small>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <strong>{consent.consented_to.name}</strong>
                                                                        <br />
                                                                        <small className="text-muted">
                                                                            {consent.consented_to.email}
                                                                        </small>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span 
                                                                        className="text-truncate d-inline-block" 
                                                                        style={{ maxWidth: '200px' }}
                                                                        title={consent.purpose}
                                                                    >
                                                                        {consent.purpose}
                                                                    </span>
                                                                </td>
                                                                <td>{getStatusBadge(consent.status)}</td>
                                                                <td>
                                                                    {consent.start_date ? 
                                                                        formatDate(consent.start_date) : 
                                                                        formatDate(consent.created_at)
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {consent.end_date ? 
                                                                        formatDate(consent.end_date) : 
                                                                        <span className="text-muted">No expiry</span>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Loading Overlay */}
                    {isLoading && (
                        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
                             style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PatientConsents;
